<template>
  <div class="fixed inset-0 z-[100] bg-black/40" @click.self="$emit('close')">
    <div class="w-full bg-header-bg dark:bg-header-bg-dark border-b border-header-border dark:border-header-border-dark shadow-lg">
      <div class="max-w-2xl mx-auto flex items-center gap-2 h-14 px-4">
        <MagnifyingGlassIcon class="h-5 w-5 text-fg-faint dark:text-fg-faint-dark flex-shrink-0" />
        <input
          ref="inputEl"
          v-model="query"
          type="text"
          :placeholder="searchPeople ? 'Search views and people…' : 'Search views…'"
          class="w-full bg-transparent outline-none text-sm text-fg-base dark:text-fg-base-dark placeholder:text-fg-faint"
          @keydown="onKeydown"
        />
      </div>
    </div>
    <div class="max-w-2xl mx-auto">
      <ul class="max-h-[70vh] overflow-y-auto py-1 mx-4 rounded-b-lg bg-surface dark:bg-surface-dark border border-t-0 border-stroke dark:border-stroke-dark shadow-xl">
        <li v-if="!items.length && !showPeople" class="px-4 py-3 text-xs text-fg-faint dark:text-fg-faint-dark">No matching views.</li>
        <template v-for="(section, si) in sections" :key="section.key">
          <li
            v-if="section.label"
            class="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-fg-faint dark:text-fg-faint-dark"
            :class="{ 'border-t border-stroke dark:border-stroke-dark mt-1': si > 0 }"
          >{{ section.label }}</li>
          <li
            v-for="item in section.items" :key="item.key"
            :ref="el => setItemEl(item.idx, el)"
            @click="select(item)"
            @mouseenter="highlightedIndex = item.idx"
            class="flex items-center justify-between gap-3 px-4 py-2 text-sm cursor-pointer transition-colors"
            :class="item.idx === highlightedIndex ? 'bg-surface-muted dark:bg-surface-hover-dark' : ''"
          >
            <span class="min-w-0 truncate">
              <span class="text-fg-base dark:text-fg-base-dark">{{ item.label }}</span>
              <span v-if="item.detail" class="ml-2 text-xs text-fg-muted dark:text-fg-muted-dark">{{ item.detail }}</span>
            </span>
            <span class="text-xs text-fg-faint dark:text-fg-faint-dark flex-shrink-0 truncate max-w-[40%]">{{ item.meta }}</span>
          </li>
        </template>
        <template v-if="showPeople && !hasPeople">
          <li class="px-4 py-2 text-xs border-t border-stroke dark:border-stroke-dark mt-1" :class="peopleError ? 'text-red-500 dark:text-red-400' : 'text-fg-faint dark:text-fg-faint-dark'">
            {{ peopleLoading ? 'Searching contacts, students, teachers…' : peopleError ? 'People search failed.' : 'No matching contacts, students, or teachers.' }}
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useApi } from '../../composables/useApi'
import { useAdminSidebar } from '../../composables/useAdminSidebar.js'
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  views: { type: Array, required: true },  // [{ to, label, group }]
  // Secondary, server-backed people search (contacts > students > teachers) —
  // only offered when the user can open those admin views (GET
  // /contacts/search has the same permission gate).
  searchPeople: { type: Boolean, default: false },
})
const emit = defineEmits(['close'])

// Where each people-result type opens; the query param is consumed by that
// view's useDeepLinkPanel() to open the record's side panel. On admin pages,
// `sidebarContact` results open in the admin sidebar in place instead.
const PEOPLE_SECTIONS = [
  { key: 'contacts', label: 'Contacts', path: '/admin/contacts/directory', param: 'contact', sidebarContact: c => c.id,
    toItem: c => ({ label: c.name || '(No name)', detail: [c.aliases.length && `aka ${c.aliases.join(', ')}`, c.students.join(', '), c.alternates?.length && `via ${c.alternates.join(', ')}`].filter(Boolean).join(' · '), meta: c.email || c.phone || '' }) },
  { key: 'students', label: 'Students', path: '/admin/contacts/students', param: 'student', sidebarContact: s => s.contact_id,
    toItem: s => ({ label: s.name, detail: s.contact_name ? `Family: ${s.contact_name}` : '', meta: s.status || '' }) },
  { key: 'teachers', label: 'Teachers', path: '/admin/contacts/teachers', param: 'teacher',
    toItem: t => ({ label: t.name, detail: t.instruments.join(', '), meta: t.hidden ? 'Hidden' : (t.email || '') }) },
]
const MIN_PEOPLE_QUERY = 2

const router = useRouter()
const { fetchApi } = useApi()
const sidebar = useAdminSidebar()

const query = ref('')
const inputEl = ref(null)
const highlightedIndex = ref(0)

// Multi-term AND match against label + group + any hidden keywords, so word
// order doesn't matter — "log s" matches "SMS Log" (label has "log", group
// "SMS" has "s") the same as "s log" or "sms log" would.
//
// Tabs (`isTab`) additionally need at least one term to hit the tab's own
// label/keywords — the group alone can narrow but never produce a tab match,
// so "billing" lists Billing's views, not every tab under them, while
// "billing cash" or "dropbox" still finds Cash & Checks.
const ownText = view => [view.label, ...(view.keywords || [])].join(' ').toLowerCase()

function matchesQuery(view, terms) {
  const own = ownText(view)
  const hay = `${own} ${view.group}`.toLowerCase()
  if (!terms.every(t => hay.includes(t))) return false
  return !view.isTab || terms.some(t => own.includes(t))
}

// Views before tabs; within each, a match on the item's own name beats one
// that only matched via its group — "payroll" puts Payroll above the rest of
// Billing & Payroll. sort() is stable, so ties keep sidebar order. Tabs never
// show on an empty query.
function rank(view, terms) {
  return (view.isTab ? 2 : 0) + (terms.every(t => ownText(view).includes(t)) ? 0 : 1)
}

const visibleViews = computed(() => {
  const terms = query.value.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (!terms.length) return props.views.filter(v => !v.isTab)
  return props.views.filter(v => matchesQuery(v, terms)).sort((a, b) => rank(a, terms) - rank(b, terms))
})

// People results arrive after the (instant) view matches, debounced, and are
// appended below them — so they never shift what's already highlighted.
const people = ref({})
const peopleLoading = ref(false)
const peopleError = ref(false)
let debounceTimer = null
let requestSeq = 0

const showPeople = computed(() => props.searchPeople && query.value.trim().length >= MIN_PEOPLE_QUERY)
const hasPeople = computed(() => PEOPLE_SECTIONS.some(sec => people.value[sec.key]?.length))

// One flat, index-numbered list across all sections, so arrow keys walk
// straight through views → contacts → students → teachers.
const sections = computed(() => {
  let idx = 0
  const out = [{
    key: 'views', label: null,
    items: visibleViews.value.map(v => ({ key: v.to, idx: idx++, to: v.to, label: v.label, meta: v.group })),
  }]
  if (showPeople.value) {
    for (const sec of PEOPLE_SECTIONS) {
      const rows = people.value[sec.key] || []
      if (!rows.length) continue
      out.push({
        key: sec.key, label: sec.label,
        items: rows.flatMap(r => {
          const item = {
            ...sec.toItem(r), key: `${sec.key}:${r.id}`, to: { path: sec.path, query: { [sec.param]: String(r.id) } },
            contactId: sec.sidebarContact?.(r) || null,
          }
          // SMS-opted-in contacts also get their text thread, listed right
          // below the Directory entry (the account page stays the default) —
          // SmsInboxTab's ?phone= opens the thread with its contact panel showing.
          const texts = sec.key === 'contacts' && r.sms_consent && r.phone
            ? [{ label: item.label, detail: 'Texts', meta: r.phone, key: `texts:${r.id}`, to: { path: '/admin/messages/texts', query: { phone: r.phone } } }]
            : []
          return [item, ...texts].map(it => ({ ...it, idx: idx++ }))
        }),
      })
    }
  }
  return out
})
const items = computed(() => sections.value.flatMap(s => s.items))

watch(query, (raw) => {
  highlightedIndex.value = 0
  clearTimeout(debounceTimer)
  const q = raw.trim()
  if (!props.searchPeople || q.length < MIN_PEOPLE_QUERY) {
    requestSeq++
    people.value = {}
    peopleLoading.value = false
    return
  }
  peopleLoading.value = true
  debounceTimer = setTimeout(async () => {
    const seq = ++requestSeq
    try {
      const data = await fetchApi(`/api/contacts/search?q=${encodeURIComponent(q)}`)
      if (seq !== requestSeq) return  // a newer query superseded this one
      people.value = data || {}
      peopleError.value = false
    } catch {
      if (seq !== requestSeq) return
      people.value = {}
      peopleError.value = true
    } finally {
      if (seq === requestSeq) peopleLoading.value = false
    }
  }, 200)
})
onBeforeUnmount(() => clearTimeout(debounceTimer))

const itemEls = []
function setItemEl(i, el) { itemEls[i] = el }
watch(highlightedIndex, (i) => nextTick(() => itemEls[i]?.scrollIntoView({ block: 'nearest' })))

onMounted(() => nextTick(() => inputEl.value?.focus()))

function select(item) {
  if (item.contactId && sidebar.available.value) sidebar.open('contact', { contactId: item.contactId })
  else router.push(item.to)
  emit('close')
}

function onKeydown(e) {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (items.value.length) highlightedIndex.value = (highlightedIndex.value + 1) % items.value.length
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (items.value.length) highlightedIndex.value = (highlightedIndex.value - 1 + items.value.length) % items.value.length
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const item = items.value[highlightedIndex.value]
    if (item) select(item)
  } else if (e.key === 'Escape' || e.key === '/') {
    // "/" closes same as it opened — the global "/" shortcut in AppRoot.vue
    // can't see this itself since it's blocked while a text input (this one)
    // is focused, so the toggle-off has to live here instead.
    e.preventDefault()
    emit('close')
  }
}
</script>
